import { CONTAINER_WIDTH_IN_MMM, CONT_LENS } from "@tedivo/tedivo-bay-grid-pure";
import { feetToMillimeters, roundDec } from "@tedivo/tedivo-pure-helpers";

import { TContainerLengths } from "open-vessel-definition";
import { TContainersSymbolsConfig } from "../../../types/TContainersSymbolsConfig";
import { colorShade } from "../../helpers/colorShade";
import { tiersShadeColor } from "./tiersShadeColor";

/**
 * The last name segment of the symbol a consumer swaps in to grey a container
 * out (BVO replaces the last `_` segment of the href with it), so every length
 * also gets a `${prefix}_tiers_${len}__invisible_` symbol.
 */
export const TIERS_INVISIBLE = "_invisible_";

export interface ITiersSymbolKey {
  len: TContainerLengths;
  count: number | typeof TIERS_INVISIBLE;
}

/** `${prefix}_tiers_${len}_${count}`: the symbol of a container of `len` feet in a stack of `count` tiers */
export function tiersSymbolName(
  prefix: string,
  len: TContainerLengths,
  count: number | typeof TIERS_INVISIBLE,
): string {
  return `${prefix}_tiers_${len}_${count}`;
}

/**
 * The series of container symbols that encode a stack's tier count as a shade
 * of `color`: one per container length and count from 0 to `maxCount`, plus
 * the grey `_invisible_` one per length. The consumer's `pathFn` draws the
 * footprint, so the shaded boxes have the same shape as its colour-by ones.
 */
export function generateTiersSymbolsConfig(
  prefixSymbol: string,
  color: string,
  maxCount: number,
  pathFn: TContainersSymbolsConfig<unknown>["pathFn"],
): TContainersSymbolsConfig<ITiersSymbolKey> {
  const top = Math.max(1, Math.floor(maxCount));

  const config: TContainersSymbolsConfig<ITiersSymbolKey> = {
    prefixSymbol,
    nameFn: (prefix, item) => (item ? tiersSymbolName(prefix, item.len, item.count) : undefined),
    getItem: () => undefined,
    pathFn,
    conts: [],
  };

  const counts: Array<number | typeof TIERS_INVISIBLE> = [
    ...Array.from({ length: top + 1 }, (_, count) => count),
    TIERS_INVISIBLE,
  ];

  CONT_LENS.forEach((len) => {
    counts.forEach((count) => {
      const fill = count === TIERS_INVISIBLE ? "#dddddd" : tiersShadeColor(color, count, top);

      config.conts.push({
        name: tiersSymbolName(prefixSymbol, len, count),
        lengthInMM: roundDec(feetToMillimeters(len), 0),
        heightInMM: CONTAINER_WIDTH_IN_MMM,
        fillColor: fill,
        strokeColor: colorShade(fill, -30),
        fillOpacity: 1,
        strokeOpacity: 1,
        strokeWidth: 2,
        strokeDashLength: 1.5,
        dashInTheMiddle: false,
      });
    });
  });

  return config;
}
